import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

import { avatarGradient, initials } from "./lib";

/**
 * The delivery-first identity mark: the last 6 digits of the tracking number
 * stacked as two rows of 3, falling back to the phone's last 3 digits, then to
 * name initials. Rows come from the digits only — separators are stripped.
 */
function avatarDigitRows(
  trackingNumber?: string | null,
  phone?: string | null,
): string[] | null {
  const tracking = (trackingNumber ?? "").replace(/\D/g, "");
  if (tracking.length > 0) {
    const last = tracking.slice(-6);
    return last.length > 3 ? [last.slice(0, -3), last.slice(-3)] : [last];
  }
  const phoneDigits = (phone ?? "").replace(/\D/g, "");
  if (phoneDigits.length > 0) return [phoneDigits.slice(-3)];
  return null;
}

export function ConversationAvatar({
  name,
  trackingNumber,
  phone,
  className,
  size = 11,
}: {
  name: string;
  trackingNumber?: string | null;
  phone?: string | null;
  className?: string;
  size?: number;
}) {
  const rows = avatarDigitRows(trackingNumber, phone);
  // `size` is in Tailwind spacing units (11 → 44px). Two stacked rows need to
  // fit, so digits scale a bit smaller than the old single-line initials.
  const pixelSize = size * 4;
  const fontSize = Math.round(
    pixelSize * (rows && rows.length > 1 ? 0.3 : 0.36),
  );

  return (
    <Avatar className={cn(`tw:size-${size}`, className)}>
      <AvatarFallback
        className="tw:font-semibold tw:text-white"
        style={{ backgroundImage: avatarGradient(name) }}
      >
        {rows ? (
          <span
            dir="ltr"
            className="tw:pointer-events-none tw:flex tw:select-none tw:flex-col tw:items-center tw:gap-px tw:tabular-nums tw:leading-none"
            style={{ fontSize }}
          >
            {rows.map((row, index) => (
              <span key={index}>{row}</span>
            ))}
          </span>
        ) : (
          <p
            className="tw:pointer-events-none tw:m-0 tw:select-none"
            style={{ fontSize: Math.round(pixelSize * 0.32) }}
          >
            {initials(name)}
          </p>
        )}
      </AvatarFallback>
    </Avatar>
  );
}
